import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Typography, Spin, Segmented, Button, Select } from 'antd';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import api from '../api';
import { useFilters } from '../contexts/FilterContext';
import { useTheme } from '../contexts/ThemeContext';
import FullscreenChart from './FullscreenChart';
import '../responsive.css';

// Modern, elegant font styling for charts
const getChartFontStyle = (isDark: boolean) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '0.3px',
  fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#4B5563'
});

const getChartLabelStyle = (isDark: boolean) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.2px',
  fill: isDark ? 'rgba(255, 255, 255, 0.85)' : '#1F2937'
});

const legendStyle = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '0.2px',
  color: '#374151'
};

// Consistent color mapping for regions - each region always gets the same color
const REGION_COLORS: { [key: string]: string } = {
  'North America': '#1890ff',      // Blue
  'Europe': '#52c41a',             // Green (matches the actual data from backend)
  'Asia Pacific': '#faad14',       // Orange
  'Middle East & Africa': '#f5222d', // Red
  'Latin & Central America': '#722ed1', // Purple
  'Eastern Europe': '#13c2c2',     // Cyan
  'Unknown': '#fa8c16'             // Dark Orange
};

// Consistent color mapping for therapeutic categories - each category gets a distinct color
const THERAPEUTIC_CATEGORY_COLORS: { [key: string]: string } = {
  // Skin Cancer indications
  'Melanoma': '#1f4e79',           // Dark Blue
  'BCC/SCC (Basal and Squamous Cell Carcinoma)': '#8884d8', // Purple
  'MCC (Merkel Cell Carcinoma)': '#82ca9d', // Green
  'Other Skin Cancers': '#ffc658', // Yellow
  
  // Dermatology indications
  'Psoriasis': '#1890ff', // Blue
  'Dermatitis': '#52c41a', // Green
  'Acne & Rosacea': '#faad14', // Orange
  'Skin Cancer': '#f5222d', // Red
  'Alopecia & Hair Disorders': '#722ed1', // Purple
  'Skin Infections': '#13c2c2', // Cyan
  'Other Conditions': '#eb2f96', // Magenta
  
  // Ophthalmology therapeutic areas (for In Sight breakdown)
  'Retinal Disorders': '#1890ff',  // Blue
  'Glaucoma': '#52c41a',           // Green
  'Dry Eye': '#faad14',            // Orange/Gold
  'Anti-Infectives': '#f5222d',    // Red
  'Allergy & Anti-Inflammatory': '#722ed1', // Purple
  'Other Ophthalmic Disorders': '#13c2c2',  // Cyan
  
  // Retinal Disorders indications
  'AMD': '#2f54eb',                // Geekblue
  'Wet AMD': '#1d39c4',           // Darker geekblue (sub of AMD)
  'Dry AMD': '#85a5ff',           // Lighter geekblue (sub of AMD)
  'Diabetic Eye Disease': '#fa541c', // Volcano
  'Retinal Vein Occlusion': '#eb2f96', // Magenta
  'Inherited Retinal Disease': '#fadb14', // Yellow
  'Other Retinal Disorders': '#a0d911', // Lime
  
  // Glaucoma indications
  'Primary Open-Angle Glaucoma (POAG)': '#1890ff', // Blue
  'POAG': '#1890ff',               // Blue
  'Primary Angle-Closure Glaucoma (PACG)': '#52c41a', // Green
  'PACG': '#52c41a',               // Green
  'Ocular Hypertension (OHT)': '#faad14', // Orange
  'OHT': '#faad14',                // Orange
  'Secondary Glaucoma': '#f5222d', // Red
  'Congenital Glaucoma': '#722ed1', // Purple
  
  'Unknown': '#fa8c16'             // Dark Orange
};

// Consistent color mapping for drug classes - each class gets a distinct color
const DRUG_CLASS_COLORS: { [key: string]: string } = {
  'Immunotherapy': '#ff4d4f',      // Red
  'Targeted Agents & Other Drugs': '#52c41a', // Green
  'Chemotherapy': '#faad14',       // Orange
  'Biologics': '#52c41a',          // Green - distinct and not used elsewhere
  'Small Molecules': '#fa8c16',    // Orange - distinct and not used elsewhere
  'Gene & Emerging Therapies': '#000000', // Black - distinct and not used elsewhere
  
  // Retinal Disorders drug classes
  'Anti-VEGF Agents': '#1890ff',   // Blue
  'Originator Anti-VEGF Drugs': '#003a8c',  // Dark blue (sub of Anti-VEGF Agents)
  'Anti-VEGF Biosimilars': '#69c0ff',       // Light blue (sub of Anti-VEGF Agents)
  'Corticosteroids': '#52c41a',    // Green
  'Gene & Cell Therapies': '#722ed1', // Purple
  'Complement Inhibitors': '#f5222d', // Red
  'Other Therapies': '#faad14',    // Orange
  
  // Glaucoma drug classes
  'Prostaglandin Analogs': '#1890ff', // Blue
  'Beta Blockers': '#52c41a',      // Green
  'Alpha Agonists': '#faad14',     // Orange
  'Carbonic Anhydrase Inhibitors': '#f5222d', // Red
  'ROCK Inhibitors': '#722ed1',    // Purple
  'Combinations & Others': '#13c2c2', // Cyan
  
  'Unknown': '#fa8c16'             // Dark Orange
};

// Color mapping for drug types
const DRUG_TYPE_COLORS: { [key: string]: string } = {
  'Small Molecules': '#1890ff',    // Blue
  'Biologics': '#722ed1',          // Purple
  'Gene & Emerging Therapies': '#52c41a', // Green
  'Other': '#13c2c2',             // Cyan
  'Unknown': '#fa8c16',           // Dark Orange
  // Dermatology-specific drug types
  'Over-the-Counter (OTC)': '#ff4d4f',  // Red - distinct from prescription
  'Prescription': '#1890ff',            // Blue - distinct from OTC
  'OTC': '#ff4d4f',                     // Red - alternative naming
  'Prescription Drugs': '#1890ff',       // Blue - alternative naming
  // Glaucoma-specific drug types (Originator vs Generic)
  'Branded': '#1890ff',                 // Blue
  'Generic': '#52c41a',                  // Green
  'Originator': '#1890ff',               // Blue - alternative naming for Branded
  'Originator vs Generic': '#1890ff'     // Blue - alternative naming
};

// Color mapping for mechanisms of action
const MECHANISM_COLORS: { [key: string]: string } = {
  // Dermatology-specific mechanism colors (Biologics)
  'IL-23': '#f759ab',                   // Pink
  'IL-17': '#eb2f96',                   // Magenta
  'IL-4/13': '#fa541c',                 // Volcano
  'TNF Inhibitors': '#faad14',          // Gold
  'IL-12/23 & Others': '#52c41a',       // Green
  // Legacy mechanism colors (for other therapeutic areas)
  'PD-1/PD-L1 Inhibitors': '#f759ab',   // Pink
  'CTLA-4 Inhibitors': '#eb2f96',       // Magenta
  'BRAF Inhibitors': '#fa541c',         // Volcano
  'MEK Inhibitors': '#faad14',          // Gold
  'Small Molecules': '#1890ff',         // Blue
  'Biologics': '#722ed1',               // Purple
  'Gene & Emerging Therapies': '#13c2c2', // Cyan
  'Other': '#52c41a',                   // Green
  'Unknown': '#fa8c16'                  // Dark Orange
};

// Color mapping for Cell, Gene & RNA Therapies submarket pillars and disease areas
const CGT_RNA_COLORS: { [key: string]: string } = {
  // Submarket pillars
  'Cell Therapy (Non-Genetically Modified)': '#1890ff',          // Blue
  'Gene-Modified Cell Therapy (GMCT / Ex Vivo)': '#722ed1',      // Purple
  'Gene Therapy (In Vivo / Direct Delivery)': '#13c2c2',         // Cyan
  'RNA & Oligonucleotide Therapeutics (In Vivo)': '#52c41a',     // Green
  // Disease therapeutic areas (alongside pillars)
  'Oncology': '#f5222d',                                         // Red
  'Hematology (Non-Oncology)': '#fa8c16',                        // Dark Orange
  'Neurology & Neuromuscular': '#faad14',                        // Gold
  'Ophthalmology': '#2f54eb',                                    // Geekblue
  'Metabolic Disorders': '#eb2f96',                              // Magenta
  'Cardiovascular': '#ff4d4f',                                   // Red
  'Immunology & Inflammation': '#52c41a',                        // Green
  'Dermatology': '#fadb14',                                      // Yellow
  'Musculoskeletal': '#a0d911',                                  // Lime
  'Other Indications': '#bfbfbf',                                // Grey
  'Unknown': '#fa8c16'                                           // Dark Orange
};

// Color mapping for small molecule drug classes
const SMALL_MOLECULE_COLORS: { [key: string]: string } = {
  // Dermatology-specific small molecule colors
  'Corticosteroids': '#2f54eb',         // Geekblue
  'Retinoids': '#1890ff',               // Blue
  'Calcineurin inhibitors': '#13c2c2',  // Cyan
  'PDE4 inhibitors': '#52c41a',         // Green
  'JAK inhibitors': '#faad14',          // Gold
  'Anti-infectives': '#f5222d',         // Red
  'Vitamin D analogues': '#722ed1',     // Purple
  'Other Small Molecules': '#fa8c16',   // Dark Orange
  // Legacy small molecule colors (for other therapeutic areas)
  'Kinase Inhibitors': '#2f54eb',       // Geekblue
  'Receptor Antagonists': '#1890ff',    // Blue
  'Enzyme Inhibitors': '#13c2c2',       // Cyan
  'Small Molecules': '#1890ff',         // Blue
  'Biologics': '#722ed1',               // Purple
  'Gene & Emerging Therapies': '#52c41a', // Green
  'Other': '#52c41a',                   // Green
  'Unknown': '#fa8c16'                  // Dark Orange
};

// Psoriasis-specific color schemes for better visual distinction
const PSORIASIS_DRUG_CLASS_COLORS: { [key: string]: string } = {
  'Interleukins': '#1890ff',           // Blue
  'TNF Inhibitors': '#52c41a',         // Green
  'Vitamin D Analogues and Topical Steroids': '#faad14', // Orange
  'Others': '#722ed1',                 // Purple
  'Unknown': '#fa8c16'                 // Dark Orange
};

const PSORIASIS_ROUTE_COLORS: { [key: string]: string } = {
  'Topical': '#ff4d4f',               // Red
  'Oral': '#13c2c2',                  // Cyan
  'Parenteral': '#fa8c16',            // Dark Orange
  'Unknown': '#fa8c16'                // Dark Orange
};

const PSORIASIS_DISTRIBUTION_COLORS: { [key: string]: string } = {
  'Point-of-Care / Clinic': '#1890ff',    // Blue
  'Retail Pharmacies': '#52c41a',         // Green
  'Digital Pharmacies': '#faad14',        // Orange
  'Direct to Consumer (D2C)': '#f5222d',  // Red
  'PoC/Clinic': '#1890ff',                // Blue (alternative naming)
  'D2C': '#f5222d',                       // Red (alternative naming)
  'Unknown': '#fa8c16'                    // Dark Orange
};

// Glaucoma/Ophthalmology distribution channel colors
const GLAUCOMA_DISTRIBUTION_COLORS: { [key: string]: string } = {
  'Hospital / Specialty Pharmacies': '#1890ff',  // Blue
  'Hospital / Specialty Pharmacy': '#1890ff',    // Blue (alternative naming)
  'Retail Pharmacy': '#52c41a',                  // Green
  'Retail Pharmacies': '#52c41a',                // Green (alternative naming)
  'Online Pharmacies / D2C': '#faad14',           // Orange
  'Hospital': '#1890ff',                          // Blue (alternative naming)
  'Specialty Clinic': '#13c2c2',                  // Cyan
  'Unknown': '#fa8c16'                            // Dark Orange
};

const OPHTHALMOLOGY_ROUTE_COLORS: { [key: string]: string } = {
  'Topical (Eye Drops / Gels / Ointments)': '#1890ff', // Blue
  'Topical': '#1890ff',                                // Alternative
  'Intravitreal': '#52c41a',                           // Green
  'Sustained-Release (SR) Implants': '#faad14',        // Orange
  'Sustained-release implants': '#faad14',             // Alternative
  'Standard injections': '#eb2f96',                    // Magenta
  'Systemic /Others': '#f5222d',                       // Red
  'Oral / Systemic / Other': '#f5222d',                // Alternative
  'Sub Retinal': '#722ed1',                            // Purple
  'Subretinal': '#722ed1',                             // Alternative
  'Unknown': '#fa8c16'                                 // Dark Orange
};

const PRESCRIPTION_STATUS_COLORS: { [key: string]: string } = {
  'Prescription (RX)': '#1890ff',                      // Blue
  'Prescription': '#1890ff',                           // Alternative
  'Over-The-Counter (OTC)': '#f5222d',                 // Red
  'OTC': '#f5222d',                                    // Alternative
  'Unknown': '#fa8c16'                                 // Dark Orange
};

const PATENT_STATUS_COLORS: { [key: string]: string } = {
  'Originator': '#1890ff',                             // Blue
  'Generic': '#52c41a',                                // Green
  'Biosimilar': '#722ed1',                             // Purple
  'Unknown': '#fa8c16'                                 // Dark Orange
};

const { Title, Text } = Typography;

// Helper function to get the appropriate color scheme based on therapeutic area and dimension
const getColorScheme = (therapeuticArea: string, dimensionMode: 'class' | 'category' | 'route' | 'distribution' | 'drugType' | 'mechanism' | 'smallMolecule' | 'prescriptionStatus' | 'patentStatus') => {
  if (therapeuticArea === 'Psoriasis') {
    switch (dimensionMode) {
      case 'class':
        return PSORIASIS_DRUG_CLASS_COLORS;
      case 'route':
        return PSORIASIS_ROUTE_COLORS;
      case 'distribution':
        return PSORIASIS_DISTRIBUTION_COLORS;
      case 'category':
        return THERAPEUTIC_CATEGORY_COLORS;
      case 'drugType':
        return DRUG_TYPE_COLORS;
      case 'mechanism':
        return MECHANISM_COLORS;
      case 'smallMolecule':
        return SMALL_MOLECULE_COLORS;
      default:
        return DRUG_CLASS_COLORS;
    }
  } else if (therapeuticArea === 'Glaucoma' || therapeuticArea === 'Glaucoma - Focus') {
    switch (dimensionMode) {
      case 'class':
        return DRUG_CLASS_COLORS;
      case 'category':
        return THERAPEUTIC_CATEGORY_COLORS;
      case 'distribution':
        return GLAUCOMA_DISTRIBUTION_COLORS;
      case 'drugType':
        return DRUG_TYPE_COLORS;
      default:
        return DRUG_CLASS_COLORS;
    }
  } else if (therapeuticArea === 'Ophthalmology' || therapeuticArea === 'Ophthalmology - In Sight' || 
             therapeuticArea === 'Retinal Disorders' || therapeuticArea === 'Retinal - Focus') {
    switch (dimensionMode) {
      case 'class':
        return DRUG_CLASS_COLORS;
      case 'category':
        return THERAPEUTIC_CATEGORY_COLORS;
      case 'route':
        return OPHTHALMOLOGY_ROUTE_COLORS;
      case 'distribution':
        return GLAUCOMA_DISTRIBUTION_COLORS;
      case 'drugType':
        return DRUG_TYPE_COLORS;
      case 'prescriptionStatus':
        return PRESCRIPTION_STATUS_COLORS;
      case 'patentStatus':
        return PATENT_STATUS_COLORS;
      default:
        return DRUG_CLASS_COLORS;
    }
  } else if (therapeuticArea === 'Cell, Gene & RNA Therapies' || therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight') {
    // 'class' = 4 submarket pillars; 'category' = 10 disease therapeutic areas.
    // Both palettes live in CGT_RNA_COLORS keyed by the exact display name.
    return CGT_RNA_COLORS;
  } else {
    switch (dimensionMode) {
      case 'class':
        return DRUG_CLASS_COLORS;
      case 'category':
        return THERAPEUTIC_CATEGORY_COLORS;
      case 'route':
        return PSORIASIS_ROUTE_COLORS;
      case 'distribution':
        return PSORIASIS_DISTRIBUTION_COLORS;
      case 'drugType':
        return DRUG_TYPE_COLORS;
      case 'mechanism':
        return MECHANISM_COLORS;
      case 'smallMolecule':
        return SMALL_MOLECULE_COLORS;
      default:
        return DRUG_CLASS_COLORS;
    }
  }
};

interface MarketOverviewProps {
  data: any;
  loading: boolean;
  activeTab?: string;
}

const MarketOverview: React.FC<MarketOverviewProps> = ({ data, loading, activeTab }) => {
  const { isDark } = useTheme();
  
  // Dynamic legend style based on theme
  const legendStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.2px',
    color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#374151'
  };
  
  const [drugClassData, setDrugClassData] = useState<any[]>([]);
  const [therapeuticData, setTherapeuticData] = useState<any[]>([]);
  const [routeData, setRouteData] = useState<any[]>([]);
  const [distributionData, setDistributionData] = useState<any[]>([]);
  const [drugTypeData, setDrugTypeData] = useState<any[]>([]);
  const [prescriptionStatusData, setPrescriptionStatusData] = useState<any[]>([]);
  const [patentStatusData, setPatentStatusData] = useState<any[]>([]);
  const [mechanismData, setMechanismData] = useState<any[]>([]);
  const [smallMoleculeData, setSmallMoleculeData] = useState<any[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);
  const { 
    filters, 
    isClassMode, 
    isCategoryMode, 
    isRouteMode, 
    isDistributionMode, 
    isDrugTypeMode,
    isMechanismMode,
    isSmallMoleculeMode,
    isPrescriptionStatusMode,
    isPatentStatusMode,
    isUnfilteredMode,
    hierarchies
  } = useFilters();
  
  // New state for dimension mode and locking
  const [dimensionMode, setDimensionMode] = useState<'class' | 'category' | 'route' | 'distribution' | 'drugType' | 'mechanism' | 'smallMolecule' | 'prescriptionStatus' | 'patentStatus'>('class');
  const [locked, setLocked] = useState(false);
  const [chartView, setChartView] = useState<'bars' | 'area'>('bars');

  // Update locking behavior based on filters
  useEffect(() => {
    if (filters.drugClasses.length > 0) {
      setDimensionMode('class');
      setLocked(true);
    } else if (filters.therapeuticCategories.length > 0) {
      setDimensionMode('category');
      setLocked(true);
    } else if (filters.routesOfAdministration.length > 0) {
      setDimensionMode('route');
      setLocked(true);
    } else if (filters.distributionChannels.length > 0) {
      setDimensionMode('distribution');
      setLocked(true);
    } else if (filters.drugTypes.length > 0) {
      setDimensionMode('drugType');
      setLocked(true);
    } else if (filters.mechanismsOfAction.length > 0) {
      setDimensionMode('mechanism');
      setLocked(true);
    } else if (filters.smallMoleculeDrugClasses.length > 0) {
      setDimensionMode('smallMolecule');
      setLocked(true);
    } else if (filters.prescriptionStatuses.length > 0) {
      setDimensionMode('prescriptionStatus');
      setLocked(true);
    } else if (filters.patentStatuses.length > 0) {
      setDimensionMode('patentStatus');
      setLocked(true);
    } else {
      setLocked(false);
    }
  }, [filters.drugClasses, filters.therapeuticCategories, filters.routesOfAdministration, filters.distributionChannels, filters.drugTypes, filters.mechanismsOfAction, filters.smallMoleculeDrugClasses, filters.prescriptionStatuses, filters.patentStatuses]);

  const isSmallMoleculeView = (filters.therapeuticArea === 'Dermatology' || filters.therapeuticArea === 'Dermatology - In Sight') &&
    dimensionMode === 'smallMolecule';

  // Auto-switch dimension mode when therapeutic area changes to prevent blank visuals
  useEffect(() => {
    if (!locked) { // Only auto-switch if not locked by filters
      const currentTA = filters.therapeuticArea;
      
      // Define available dimensions for each therapeutic area
      const availableDimensions: { [key: string]: string[] } = {
        // Dermatology domain
        'Psoriasis': ['class', 'route', 'distribution'],
        'Dermatology': ['category', 'route', 'drugType', 'distribution', 'class', 'mechanism', 'smallMolecule'],
        'Skin Cancer': ['class', 'category'],
        'Skin Cancer - Trial Version': ['class', 'category'],
        // Ophthalmology domain
        'Ophthalmology': ['category', 'route', 'distribution', 'drugType', 'prescriptionStatus', 'patentStatus'],
        'Ophthalmology - In Sight': ['category', 'route', 'distribution', 'drugType', 'prescriptionStatus', 'patentStatus'],
        'Retinal Disorders': ['category', 'class', 'route', 'distribution'],
        'Retinal - Focus': ['category', 'class', 'route', 'distribution'],
        'Glaucoma': ['class', 'category', 'distribution', 'drugType'],
        'Glaucoma - Focus': ['class', 'category', 'distribution', 'drugType'],
        // Cell, Gene & RNA domain - In Sight v1:
        //   class    = the 4 submarket pillars (treatment modality)
        //   category = the 10 disease therapeutic areas
        // Both sum to the same total market; the chart picks drugClassData first
        // so the unfiltered Total Market Size chart sums the 4 pillars.
        'Cell, Gene & RNA Therapies': ['class', 'category'],
        'Cell, Gene & RNA Therapies - In Sight': ['class', 'category']
      };
      
      const currentDimensions = availableDimensions[currentTA as keyof typeof availableDimensions] || ['class'];
      
      // If current dimension is not available in the new therapeutic area, switch to the first available one
      if (!currentDimensions.includes(dimensionMode)) {
        setDimensionMode(currentDimensions[0] as any);
      }
    }
  }, [filters.therapeuticArea, dimensionMode, locked]);

  // Helper to build reverse hierarchy (parent → children map) from hierarchy dict
  const buildReverseHierarchy = (dimensionKey: string): { [parent: string]: string[] } => {
    const h = hierarchies[dimensionKey] || {};
    const reverse: { [parent: string]: string[] } = {};
    Object.entries(h).forEach(([child, parent]) => {
      if (!reverse[parent]) reverse[parent] = [];
      reverse[parent].push(child);
    });
    return reverse;
  };

  // Compute stack groups for bar charts: children of the same parent stack together
  // Only stacks in default mode (no filter active); when filter selects a parent, children show as separate bars
  const getStackGroups = (mode: string): { [childKey: string]: string } => {
    const dimensionKey = mode === 'class' ? 'drug_classes' :
                         mode === 'category' ? 'therapeutic_categories' :
                         mode === 'route' ? 'routes_of_administration' : '';
    if (!dimensionKey) return {};

    const dimHierarchy = hierarchies[dimensionKey] || {};
    
    // Only stack in default (unfiltered) mode
    const filterActive =
      (mode === 'class' && filters.drugClasses.length > 0) ||
      (mode === 'category' && filters.therapeuticCategories.length > 0) ||
      (mode === 'route' && filters.routesOfAdministration.length > 0);

    if (filterActive) return {}; // No stacking when filtered → show separate bars for drill-down

    const stackGroups: { [childKey: string]: string } = {};
    Object.entries(dimHierarchy).forEach(([child, parent]) => {
      stackGroups[child] = parent;
    });
    return stackGroups;
  };

  // Centralized data aggregation utilities
  const getTotalsForMode = (filters: any, drugClassData: any[], therapeuticData: any[], routeData: any[] = [], distributionData: any[] = [], drugTypeData: any[] = [], mechanismData: any[] = [], smallMoleculeData: any[] = [], prescriptionStatusData: any[] = [], patentStatusData: any[] = []) => {
    // Helper to filter out subcategories when summing
    const sumWithoutDoubleCounting = (yearData: any, dimensionKey: string) => {
      const dimensionHierarchy = hierarchies[dimensionKey] || {};
      return Object.keys(yearData)
        .filter(key => key !== 'year' && !dimensionHierarchy[key]) // Only sum if not a subcategory
        .reduce((sum, key) => sum + (yearData[key] || 0), 0);
    };

    if (isClassMode && filters.drugClasses.length > 0) {
      // Sum across selected drug classes only
      return drugClassData.map(yearData => {
        const totalRevenue = filters.drugClasses.reduce((sum: number, drugClass: string) => {
          return sum + (yearData[drugClass] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isCategoryMode && filters.therapeuticCategories.length > 0) {
      // Sum across selected therapeutic categories only
      return therapeuticData.map(yearData => {
        const totalRevenue = filters.therapeuticCategories.reduce((sum: number, category: string) => {
          return sum + (yearData[category] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isRouteMode && filters.routesOfAdministration.length > 0) {
      // Sum across selected routes of administration only
      return routeData.map(yearData => {
        const totalRevenue = filters.routesOfAdministration.reduce((sum: number, route: string) => {
          return sum + (yearData[route] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isDistributionMode && filters.distributionChannels.length > 0) {
      // Sum across selected distribution channels only
      return distributionData.map(yearData => {
        const totalRevenue = filters.distributionChannels.reduce((sum: number, channel: string) => {
          return sum + (yearData[channel] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isDrugTypeMode && filters.drugTypes.length > 0) {
      // Sum across selected drug types only
      return drugTypeData.map(yearData => {
        const totalRevenue = filters.drugTypes.reduce((sum: number, drugType: string) => {
          return sum + (yearData[drugType] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isMechanismMode && filters.mechanismsOfAction.length > 0) {
      // Sum across selected mechanisms of action only
      return mechanismData.map(yearData => {
        const totalRevenue = filters.mechanismsOfAction.reduce((sum: number, mechanism: string) => {
          return sum + (yearData[mechanism] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isSmallMoleculeMode && filters.smallMoleculeDrugClasses.length > 0) {
      // Sum across selected small molecule drug classes only
      return smallMoleculeData.map(yearData => {
        const totalRevenue = filters.smallMoleculeDrugClasses.reduce((sum: number, smallMoleculeClass: string) => {
          return sum + (yearData[smallMoleculeClass] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isPrescriptionStatusMode && filters.prescriptionStatuses.length > 0) {
      // Sum across selected prescription statuses only
      return prescriptionStatusData.map(yearData => {
        const totalRevenue = filters.prescriptionStatuses.reduce((sum: number, status: string) => {
          return sum + (yearData[status] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isPatentStatusMode && filters.patentStatuses.length > 0) {
      // Sum across selected patent statuses only
      return patentStatusData.map(yearData => {
        const totalRevenue = filters.patentStatuses.reduce((sum: number, status: string) => {
          return sum + (yearData[status] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else {
      // Sum across all available data (grand total) - avoiding double counting
      const sourceData = drugClassData.length > 0 ? drugClassData : 
                        therapeuticData.length > 0 ? therapeuticData :
                        routeData.length > 0 ? routeData : 
                        distributionData.length > 0 ? distributionData :
                        drugTypeData.length > 0 ? drugTypeData :
                        mechanismData.length > 0 ? mechanismData :
                        smallMoleculeData.length > 0 ? smallMoleculeData :
                        prescriptionStatusData.length > 0 ? prescriptionStatusData :
                        patentStatusData.length > 0 ? patentStatusData : [];
      
      const dimensionKey = drugClassData.length > 0 ? 'drug_classes' :
                          therapeuticData.length > 0 ? 'therapeutic_categories' :
                          routeData.length > 0 ? 'routes_of_administration' :
                          distributionData.length > 0 ? 'distribution_channels' :
                          drugTypeData.length > 0 ? 'drug_types' :
                          mechanismData.length > 0 ? 'mechanisms_of_action' :
                          smallMoleculeData.length > 0 ? 'small_molecule_drug_classes' :
                          prescriptionStatusData.length > 0 ? 'prescription_statuses' :
                          patentStatusData.length > 0 ? 'patent_statuses' : '';

      return sourceData.map(yearData => {
        const totalRevenue = sumWithoutDoubleCounting(yearData, dimensionKey);
        return { year: yearData.year, totalRevenue };
      });
    }
  };

  const fetchChartData = React.useCallback(async () => {
    try {
      setChartLoading(true);
      
      // Build query parameters based on filters
      // Defensive check for yearRange
      const safeYearRange = Array.isArray(filters.yearRange) && filters.yearRange.length >= 2 
        ? filters.yearRange 
        : [2019, 2025]; // Default for trial version
      
      const params = new URLSearchParams({
        start_year: safeYearRange[0].toString(),
        end_year: safeYearRange[1].toString()
      });

      // Add therapeutic area parameter
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }

      // Add therapeutic category filter if specific categories are selected
      if (filters.therapeuticCategories.length > 0) {
        filters.therapeuticCategories.forEach(category => {
          params.append('therapeutic_categories', category);
        });
        console.log('Therapeutic categories filter applied:', filters.therapeuticCategories);
      } else {
        console.log('No therapeutic categories filter applied - showing all');
      }
      // If no therapeutic categories selected, don't apply filter (show all categories)

      // Add drug class filter if specific drug classes are selected
      if (filters.drugClasses.length > 0) {
        filters.drugClasses.forEach(drugClass => {
          params.append('drug_classes', drugClass);
        });
        console.log('Drug classes filter applied:', filters.drugClasses);
      }

      // Add drug type filter
      if (filters.drugTypes.length > 0) {
        filters.drugTypes.forEach(drugType => {
          params.append('drug_types', drugType);
        });
        console.log('Drug types filter applied:', filters.drugTypes);
      }

      // Add mechanism of action filter
      if (filters.mechanismsOfAction.length > 0) {
        filters.mechanismsOfAction.forEach(mechanism => {
          params.append('mechanisms_of_action', mechanism);
        });
        console.log('Mechanisms filter applied:', filters.mechanismsOfAction);
      }

      // Add small molecule drug class filter
      if (filters.smallMoleculeDrugClasses.length > 0) {
        filters.smallMoleculeDrugClasses.forEach(smallMoleculeClass => {
          params.append('small_molecule_classes', smallMoleculeClass);
        });
        console.log('Small molecule classes filter applied:', filters.smallMoleculeDrugClasses);
      }

      // Add prescription status filter
      if (filters.prescriptionStatuses.length > 0) {
        filters.prescriptionStatuses.forEach(status => {
          params.append('statuses', status);
        });
        console.log('Prescription statuses filter applied:', filters.prescriptionStatuses);
      }

      // Add patent status filter
      if (filters.patentStatuses.length > 0) {
        filters.patentStatuses.forEach(status => {
          params.append('statuses', status);
        });
        console.log('Patent statuses filter applied:', filters.patentStatuses);
      }

      console.log('API params:', params.toString());

      // Fetch revenue by drug class
      const drugClassResponse = await api.get(`/api/market/revenue-by-class?${params}`);
      setDrugClassData(transformChartData(drugClassResponse.data));

      // Fetch revenue by therapeutic category
      const therapeuticResponse = await api.get(`/api/market/revenue-by-therapeutic?${params}`);
      setTherapeuticData(transformChartData(therapeuticResponse.data));


      // Fetch route data for Psoriasis, Dermatology, and Ophthalmology areas
      const ophthalmologyAreas = ['Ophthalmology', 'Ophthalmology - In Sight', 'Retinal Disorders', 'Retinal - Focus', 'Glaucoma', 'Glaucoma - Focus'];
      if (filters.therapeuticArea === 'Psoriasis' || filters.therapeuticArea === 'Dermatology' || ophthalmologyAreas.includes(filters.therapeuticArea)) {
        try {
          // Fetch revenue by route of administration
          const routeResponse = await api.get(`/api/market/revenue-by-route?${params}`);
          setRouteData(transformChartData(routeResponse.data));
          console.log('Route data updated:', routeResponse.data);
        } catch (error) {
          console.warn('Failed to fetch route data:', error);
          setRouteData([]);
        }
      } else {
        // Clear route data for other therapeutic areas
        setRouteData([]);
      }

      // Fetch distribution channel data for all therapeutic areas that support it
      // This includes Psoriasis, Dermatology, Glaucoma, and other ophthalmology areas
      try {
        const distributionResponse = await api.get(`/api/market/revenue-by-distribution?${params}`);
        setDistributionData(transformChartData(distributionResponse.data));
        console.log('Distribution data updated:', distributionResponse.data);
      } catch (error) {
        console.warn('Failed to fetch distribution data:', error);
        setDistributionData([]);
      }

      // Always fetch drug type, mechanism, and small molecule data for all therapeutic areas
      // These endpoints should work for all therapeutic areas
      try {
        const drugTypeResponse = await api.get(`/api/market/revenue-by-drug-type?${params}`);
        setDrugTypeData(transformChartData(drugTypeResponse.data));
        console.log('Drug type data updated:', drugTypeResponse.data);
      } catch (error) {
        console.warn('Failed to fetch drug type data:', error);
        setDrugTypeData([]);
      }

      try {
        const mechanismResponse = await api.get(`/api/market/revenue-by-mechanism?${params}`);
        setMechanismData(transformChartData(mechanismResponse.data));
        console.log('Mechanism data updated:', mechanismResponse.data);
      } catch (error) {
        console.warn('Failed to fetch mechanism data:', error);
        setMechanismData([]);
      }

      try {
        const smallMoleculeResponse = await api.get(`/api/market/revenue-by-small-molecule?${params}`);
        setSmallMoleculeData(transformChartData(smallMoleculeResponse.data));
        console.log('Small molecule data updated:', smallMoleculeResponse.data);
      } catch (error) {
        console.warn('Failed to fetch small molecule data:', error);
        setSmallMoleculeData([]);
      }

      // Fetch prescription and patent status data
      if (ophthalmologyAreas.includes(filters.therapeuticArea)) {
        try {
          const prescriptionResponse = await api.get(`/api/market/revenue-by-prescription-status?${params}`);
          setPrescriptionStatusData(transformChartData(prescriptionResponse.data));
        } catch (error) {
          console.warn('Failed to fetch prescription status data:', error);
          setPrescriptionStatusData([]);
        }

        try {
          const patentResponse = await api.get(`/api/market/revenue-by-patent-status?${params}`);
          setPatentStatusData(transformChartData(patentResponse.data));
        } catch (error) {
          console.warn('Failed to fetch patent status data:', error);
          setPatentStatusData([]);
        }
      } else {
        setPrescriptionStatusData([]);
        setPatentStatusData([]);
      }

      console.log('Therapeutic data updated:', therapeuticResponse.data);

    } catch (error) {
      console.error('Error fetching chart data:', error);
      // NO MOCK DATA - ONLY REAL DATABASE DATA
      setDrugClassData([]);
      setTherapeuticData([]);
      setRouteData([]);
      setDistributionData([]);
    } finally {
      setChartLoading(false);
    }
  }, [filters.yearRange, filters.therapeuticCategories, filters.drugClasses, filters.routesOfAdministration, filters.distributionChannels, filters.therapeuticArea, filters.drugTypes, filters.mechanismsOfAction, filters.smallMoleculeDrugClasses]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  // Debug: Log when therapeutic data changes
  useEffect(() => {
    console.log('Therapeutic data state changed:', therapeuticData);
  }, [therapeuticData]);


  const transformChartData = (chartResponse: any) => {
    if (!chartResponse.series || chartResponse.series.length === 0) {
      return [];
    }

    // Group data by year
    const dataByYear: any = {};
    
    chartResponse.series.forEach((series: any) => {
      series.data.forEach((point: any) => {
        const year = point.x;
        if (!dataByYear[year]) {
          dataByYear[year] = { year };
        }
        dataByYear[year][series.name] = point.y;
      });
    });

    return Object.values(dataByYear);
  };

  // New helper functions for data aggregation
  const getTotalsForSlice = (filters: any, drugClassData: any[], therapeuticData: any[], routeData: any[] = [], distributionData: any[] = [], drugTypeData: any[] = [], mechanismData: any[] = [], smallMoleculeData: any[] = [], prescriptionStatusData: any[] = [], patentStatusData: any[] = []) => {
    if (isClassMode && filters.drugClasses.length > 0) {
      // Sum across selected drug classes only
      return drugClassData.map(yearData => {
        const totalRevenue = filters.drugClasses.reduce((sum: number, drugClass: string) => {
          return sum + (yearData[drugClass] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isCategoryMode && filters.therapeuticCategories.length > 0) {
      // Sum across selected therapeutic categories only
      return therapeuticData.map(yearData => {
        const totalRevenue = filters.therapeuticCategories.reduce((sum: number, category: string) => {
          return sum + (yearData[category] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isRouteMode && filters.routesOfAdministration.length > 0) {
      return routeData.map(yearData => {
        const totalRevenue = filters.routesOfAdministration.reduce((sum: number, route: string) => {
          return sum + (yearData[route] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isDistributionMode && filters.distributionChannels.length > 0) {
      return distributionData.map(yearData => {
        const totalRevenue = filters.distributionChannels.reduce((sum: number, channel: string) => {
          return sum + (yearData[channel] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isDrugTypeMode && filters.drugTypes.length > 0) {
      return drugTypeData.map(yearData => {
        const totalRevenue = filters.drugTypes.reduce((sum: number, type: string) => {
          return sum + (yearData[type] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isMechanismMode && filters.mechanismsOfAction.length > 0) {
      return mechanismData.map(yearData => {
        const totalRevenue = filters.mechanismsOfAction.reduce((sum: number, mechanism: string) => {
          return sum + (yearData[mechanism] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isSmallMoleculeMode && filters.smallMoleculeDrugClasses.length > 0) {
      return smallMoleculeData.map(yearData => {
        const totalRevenue = filters.smallMoleculeDrugClasses.reduce((sum: number, sm: string) => {
          return sum + (yearData[sm] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isPrescriptionStatusMode && filters.prescriptionStatuses.length > 0) {
      return prescriptionStatusData.map(yearData => {
        const totalRevenue = filters.prescriptionStatuses.reduce((sum: number, status: string) => {
          return sum + (yearData[status] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else if (isPatentStatusMode && filters.patentStatuses.length > 0) {
      return patentStatusData.map(yearData => {
        const totalRevenue = filters.patentStatuses.reduce((sum: number, status: string) => {
          return sum + (yearData[status] || 0);
        }, 0);
        return { year: yearData.year, totalRevenue };
      });
    } else {
      // Sum across all available data (grand total)
      const sourceData = drugClassData.length > 0 ? drugClassData : 
                        therapeuticData.length > 0 ? therapeuticData :
                        routeData.length > 0 ? routeData : 
                        distributionData.length > 0 ? distributionData :
                        drugTypeData.length > 0 ? drugTypeData :
                        mechanismData.length > 0 ? mechanismData :
                        smallMoleculeData.length > 0 ? smallMoleculeData :
                        prescriptionStatusData.length > 0 ? prescriptionStatusData :
                        patentStatusData.length > 0 ? patentStatusData : [];
      
      if (sourceData.length === 0) return [];
      
      return sourceData.map(yearData => {
        const totalRevenue = Object.keys(yearData)
          .filter(key => key !== 'year')
          .reduce((sum, key) => sum + (yearData[key] || 0), 0);
        return { year: yearData.year, totalRevenue };
      });
    }
  };

  const getYoYFromTotals = (totals: any[]) => {
    if (!totals || totals.length < 2) return [];
    
    return totals.map((yearData, index) => {
      if (index === 0) {
        return {
          year: yearData.year,
          yoyGrowth: null,
          absoluteDelta: null,
          revenue: yearData.totalRevenue
        };
      }
      
      const previousRevenue = totals[index - 1].totalRevenue;
      const currentRevenue = yearData.totalRevenue;
      const yoyGrowth = previousRevenue > 0 ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : null;
      const absoluteDelta = currentRevenue - previousRevenue;
      
      return {
        year: yearData.year,
        yoyGrowth: yoyGrowth,
        absoluteDelta: absoluteDelta,
        revenue: currentRevenue
      };
    });
  };

  const getCagrFromTotals = (totals: any[], startYear: number, endYear: number) => {
    if (!totals || totals.length < 2) return null;
    
    const firstYear = totals[0];
    const lastYear = totals[totals.length - 1];
    const periods = lastYear.year - firstYear.year;
    
    if (periods === 0 || firstYear.totalRevenue <= 0) return null;
    
    const cagr = Math.pow(lastYear.totalRevenue / firstYear.totalRevenue, 1 / periods) - 1;
    return cagr * 100; // Convert to percentage
  };

  const getDimensionTrend = (filters: any, dimensionMode: 'class' | 'category' | 'route' | 'distribution' | 'drugType' | 'mechanism' | 'smallMolecule' | 'prescriptionStatus' | 'patentStatus') => {
    let sourceData: any[] = [];
    
    if (dimensionMode === 'class') {
      sourceData = drugClassData;
    } else if (dimensionMode === 'category') {
      sourceData = therapeuticData;
    } else if (dimensionMode === 'route') {
      sourceData = routeData;
    } else if (dimensionMode === 'distribution') {
      sourceData = distributionData;
    } else if (dimensionMode === 'drugType') {
      sourceData = drugTypeData;
    } else if (dimensionMode === 'mechanism') {
      sourceData = mechanismData;
    } else if (dimensionMode === 'smallMolecule') {
      sourceData = smallMoleculeData;
    } else if (dimensionMode === 'prescriptionStatus') {
      sourceData = prescriptionStatusData;
    } else if (dimensionMode === 'patentStatus') {
      sourceData = patentStatusData;
    }
    
    if (!sourceData || sourceData.length === 0) return [];
    
    return sourceData.map(yearData => {
      const result: any = { year: yearData.year };
      const dimensionHierarchy = (dimensionMode === 'class' ? hierarchies['drug_classes'] :
                                 dimensionMode === 'category' ? hierarchies['therapeutic_categories'] :
                                 dimensionMode === 'route' ? hierarchies['routes_of_administration'] : {}) || {};
      
      if (dimensionMode === 'class') {
        const reverseH = buildReverseHierarchy('drug_classes');
        if (filters.drugClasses.length > 0) {
          // Filter mode: auto-expand parents to children for drill-down
          filters.drugClasses.forEach((drugClass: string) => {
            const children = reverseH[drugClass];
            if (children && children.length > 0) {
              // Parent selected → show children as separate series
              children.forEach(child => {
                result[child] = yearData[child] || 0;
              });
            } else {
              result[drugClass] = yearData[drugClass] || 0;
            }
          });
        } else {
          // Default: replace parents with their children for stacked bars
          Object.keys(yearData).forEach(key => {
            if (key === 'year') return;
            if (dimensionHierarchy[key]) return; // Skip children (added via parent below)
            const children = reverseH[key];
            if (children && children.length > 0) {
              // Parent with children: add children (they'll stack visually)
              children.forEach(child => {
                result[child] = yearData[child] || 0;
              });
            } else {
              result[key] = yearData[key] || 0;
            }
          });
        }
      } else if (dimensionMode === 'category') {
        const reverseH = buildReverseHierarchy('therapeutic_categories');
        if (filters.therapeuticCategories.length > 0) {
          // Filter mode: auto-expand parents to children for drill-down
          filters.therapeuticCategories.forEach((category: string) => {
            const children = reverseH[category];
            if (children && children.length > 0) {
              children.forEach(child => {
                result[child] = yearData[child] || 0;
              });
            } else {
              result[category] = yearData[category] || 0;
            }
          });
        } else {
          // Default: replace parents with their children for stacked bars
          Object.keys(yearData).forEach(key => {
            if (key === 'year') return;
            if (dimensionHierarchy[key]) return;
            const children = reverseH[key];
            if (children && children.length > 0) {
              children.forEach(child => {
                result[child] = yearData[child] || 0;
              });
            } else {
              result[key] = yearData[key] || 0;
            }
          });
        }
      } else if (dimensionMode === 'route') {
        const reverseH = buildReverseHierarchy('routes_of_administration');
        if (filters.routesOfAdministration.length > 0) {
          // Filter mode: auto-expand parents to children for drill-down
          filters.routesOfAdministration.forEach((route: string) => {
            const children = reverseH[route];
            if (children && children.length > 0) {
              children.forEach(child => {
                result[child] = yearData[child] || 0;
              });
            } else {
              result[route] = yearData[route] || 0;
            }
          });
        } else {
          // Default: replace parents with their children for stacked bars
          Object.keys(yearData).forEach(key => {
            if (key === 'year') return;
            if (dimensionHierarchy[key]) return;
            const children = reverseH[key];
            if (children && children.length > 0) {
              children.forEach(child => {
                result[child] = yearData[child] || 0;
              });
            } else {
              result[key] = yearData[key] || 0;
            }
          });
        }
      } else if (dimensionMode === 'distribution') {
        if (filters.distributionChannels.length > 0) {
          // Show only selected distribution channels
          filters.distributionChannels.forEach((channel: string) => {
            result[channel] = yearData[channel] || 0;
          });
        } else {
          // Show all distribution channels
          Object.keys(yearData).forEach(key => {
            if (key !== 'year') {
              result[key] = yearData[key] || 0;
            }
          });
        }
      } else if (dimensionMode === 'drugType') {
        if (filters.drugTypes.length > 0) {
          // Show only selected drug types
          filters.drugTypes.forEach((drugType: string) => {
            result[drugType] = yearData[drugType] || 0;
          });
        } else {
          // Show all drug types
          Object.keys(yearData).forEach(key => {
            if (key !== 'year') {
              result[key] = yearData[key] || 0;
            }
          });
        }
      } else if (dimensionMode === 'mechanism') {
        if (filters.mechanismsOfAction.length > 0) {
          // Show only selected mechanisms of action
          filters.mechanismsOfAction.forEach((mechanism: string) => {
            result[mechanism] = yearData[mechanism] || 0;
          });
        } else {
          // Show all mechanisms of action
          Object.keys(yearData).forEach(key => {
            if (key !== 'year') {
              result[key] = yearData[key] || 0;
            }
          });
        }
      } else if (dimensionMode === 'smallMolecule') {
        if (filters.smallMoleculeDrugClasses.length > 0) {
          // Show only selected small molecule drug classes
          filters.smallMoleculeDrugClasses.forEach((smallMoleculeClass: string) => {
            result[smallMoleculeClass] = yearData[smallMoleculeClass] || 0;
          });
        } else {
          // Show all small molecule drug classes
          Object.keys(yearData).forEach(key => {
            if (key !== 'year') {
              result[key] = yearData[key] || 0;
            }
          });
        }
      } else if (dimensionMode === 'prescriptionStatus') {
        if (filters.prescriptionStatuses.length > 0) {
          // Show only selected prescription statuses
          filters.prescriptionStatuses.forEach((status: string) => {
            result[status] = yearData[status] || 0;
          });
        } else {
          // Show all prescription statuses
          Object.keys(yearData).forEach(key => {
            if (key !== 'year') {
              result[key] = yearData[key] || 0;
            }
          });
        }
      } else if (dimensionMode === 'patentStatus') {
        if (filters.patentStatuses.length > 0) {
          // Show only selected patent statuses
          filters.patentStatuses.forEach((status: string) => {
            result[status] = yearData[status] || 0;
          });
        } else {
          // Show all patent statuses
          Object.keys(yearData).forEach(key => {
            if (key !== 'year') {
              result[key] = yearData[key] || 0;
            }
          });
        }
      }
      
      return result;
    });
  };

  // Helper function to create multi-line labels for long names
  const createMultiLineLabel = (name: string): string => {
    // Handle specific known long names with logical breaks
    const specificBreaks: { [key: string]: string } = {
      'Vitamin D Analogues and Topical Steroids': 'Vitamin D Analogues\nand Topical Steroids',
      'BCC/SCC (Basal and Squamous Cell Carcinoma)': 'BCC/SCC (Basal and\nSquamous Cell Carcinoma)',
      'MCC (Merkel Cell Carcinoma)': 'MCC (Merkel Cell\nCarcinoma)',
      'Direct to Consumer (D2C)': 'Direct to Consumer\n(D2C)',
      'Point-of-Care / Clinic': 'Point-of-Care\n/ Clinic'
    };
    
    return specificBreaks[name] || name;
  };

  // Custom label component with intelligent positioning
  // Custom tooltip styling
  const CustomTooltip = ({ active, payload, isDark }: any) => {
    if (active && payload && payload.length) {
      const fullName = payload[0].payload?.fullName || payload[0].name;
      return (
        <div style={{
          backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
          border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          fontSize: '14px',
          fontWeight: 500
        }}>
          <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>{fullName}</div>
          <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '16px', fontWeight: 600 }}>
            ${Number(payload[0].value).toFixed(1)}M
          </div>
        </div>
      );
    }
    return null;
  };

  /** Stacked breakdown trend (bars/area): many series; keep full list visible via scroll + parent overflow fix */
  const renderBreakdownTrendTooltip = (tooltipProps: any) => {
    const { active, payload, label, coordinate, viewBox } = tooltipProps;
    if (!active || !payload?.length) return null;

    const total = payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0);
    const sortedPayload = [...payload].sort(
      (a: any, b: any) => (Number(b.value) || 0) - (Number(a.value) || 0)
    );

    const mouseX = coordinate?.x ?? 0;
    const chartWidth = viewBox?.width ?? 600;
    const tooltipWidth = 300;
    const isLeftSide = mouseX > chartWidth / 2;
    const tooltipX = isLeftSide ? mouseX - tooltipWidth - 20 : mouseX + 20;
    const tooltipY = -10;

    return (
      <div
        style={{
          backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
          border: `2px solid ${isDark ? '#434343' : '#d9d9d9'}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.15)',
          position: 'absolute',
          left: tooltipX,
          top: tooltipY,
          zIndex: 10000,
          maxWidth: '320px',
          minWidth: '200px',
          pointerEvents: 'none',
        }}
      >
        <p
          style={{
            margin: '0 0 8px 0',
            fontWeight: 'bold',
            fontSize: '14px',
            color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333',
          }}
        >
          Year: {label}
        </p>
        <div
          style={{
            maxHeight: 'min(380px, 55vh)',
            overflowY: 'auto',
            pointerEvents: 'auto',
            paddingRight: '6px',
          }}
        >
          {sortedPayload.map((entry: any, index: number) => (
            <p
              key={index}
              style={{
                margin: '6px 0',
                color: entry.color,
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              {entry.name}: ${Number(entry.value).toFixed(1)}M
            </p>
          ))}
        </div>
        <p
          style={{
            margin: '10px 0 0 0',
            fontWeight: 'bold',
            borderTop: `2px solid ${isDark ? '#434343' : '#e8e8e8'}`,
            paddingTop: '8px',
            fontSize: '14px',
            color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333',
          }}
        >
          Total: ${total.toFixed(1)}M
        </p>
      </div>
    );
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, payload }: any) => {
    const RADIAN = Math.PI / 180;
    const isSmallMolecules = isSmallMoleculeView;
    const baseRadiusMultiplier = isSmallMolecules ? 2.2 : 1.4;
    const radius = innerRadius + (outerRadius - innerRadius) * baseRadiusMultiplier;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const percentage = `${(percent * 100).toFixed(1)}%`;
    const sliceColor = payload?.color || '#8884d8';

    let adjustedX = x;
    let adjustedY = y;

    if (isSmallMolecules && typeof midAngle !== 'undefined') {
      const normalizedAngle = midAngle < 0 ? midAngle + 360 : midAngle;
      const extraDistance = percent < 0.05 ? 20 : percent < 0.08 ? 12 : 0;
      const offset = (() => {
        if (normalizedAngle >= 0 && normalizedAngle < 90) return { dx: 35 + extraDistance, dy: -25 - extraDistance / 2 };
        if (normalizedAngle >= 270 && normalizedAngle < 360) return { dx: -35 - extraDistance, dy: -25 - extraDistance / 2 };
        if (normalizedAngle >= 90 && normalizedAngle < 180) return { dx: 35 + extraDistance, dy: 25 + extraDistance / 2 };
        return { dx: -35 - extraDistance, dy: 25 + extraDistance / 2 };
      })();
      adjustedX += offset.dx;
      adjustedY += offset.dy;
    }

    if (name.includes('Corticosteroids')) {
      adjustedY += 10;
    }
    if (name.includes('Retinoids')) {
      adjustedY -= 20;
      adjustedX += 20;
    }
    if (name.includes('Calcineurin inhibitors')) {
      adjustedY += 12;
    }
    if (name.includes('Other Small Molecules')) {
      adjustedX += 10;
    }
    if (name.includes('Anti-infectives')) {
      adjustedX += 55;
    }

    if ((filters.therapeuticArea === 'Psoriasis' || filters.therapeuticArea === 'Psoriasis - Focus') &&
        (name.includes('Interleukins') || name.includes('Interleukin'))) {
      adjustedY = y - 20;
    } else if ((filters.therapeuticArea === 'Dermatology' || filters.therapeuticArea === 'Dermatology - In Sight') &&
               (name.includes('Small Molecules') || name.includes('Small Molecule'))) {
      adjustedY = Math.min(adjustedY, y - 20);
    } else if (name.includes('Point-of-Care') || name.includes('Point-of-Care / Clinic')) {
      adjustedX = x + 30;
    } else if (x > cx) {
      adjustedX = x - 10;
    } else if (x < cx) {
      adjustedX = x - 15;
    }

    if (isSmallMolecules) {
      if (name.includes('Corticosteroids')) {
        adjustedY += 15;
      } else if (name.includes('Retinoids')) {
        adjustedY -= 20;
        adjustedX += 10;
      } else if (name.includes('Other Small Molecules')) {
        adjustedX += 10;
      }
    }

    // Apply pre-computed anti-collision offset. Pushes each label further away from
    // its neighbour on the same side of the chart so labels never overlap when many
    // small slices cluster together (e.g. CGT/RNA "Therapeutic Area" view with 10
    // disease cuts).
    const collisionDy = labelDyMap.get(name) || 0;
    if (collisionDy !== 0) {
      adjustedY += collisionDy;
    }

    const lines = name.split('\n');

    const renderTspan = (content: string, index: number, opacity = 1) => (
      <tspan
        key={index}
        x={adjustedX}
        dy={index === 0 ? 0 : '1.2em'}
        fill={sliceColor}
        opacity={opacity}
        style={{
                                  fontFamily: getChartLabelStyle(isDark).fontFamily,
                                  fontSize: getChartLabelStyle(isDark).fontSize,
                                  fontWeight: getChartLabelStyle(isDark).fontWeight,
                                  letterSpacing: getChartLabelStyle(isDark).letterSpacing
        }}
      >
        {content}
      </tspan>
    );

    return (
      <text
        x={adjustedX}
        y={adjustedY}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fill={sliceColor}
        style={{
                                  fontFamily: getChartLabelStyle(isDark).fontFamily,
                                  fontSize: getChartLabelStyle(isDark).fontSize,
                                  fontWeight: getChartLabelStyle(isDark).fontWeight,
                                  letterSpacing: getChartLabelStyle(isDark).letterSpacing
        }}
      >
        {lines.map((line: string, index: number) => renderTspan(line, index))}
        {renderTspan(percentage, lines.length, 0.7)}
      </text>
    );
  };

  const getDimensionEndYearComposition = (filters: any, dimensionMode: 'class' | 'category' | 'route' | 'distribution' | 'drugType' | 'mechanism' | 'smallMolecule' | 'prescriptionStatus' | 'patentStatus') => {
    let sourceData: any[] = [];
    
    if (dimensionMode === 'class') {
      sourceData = drugClassData;
    } else if (dimensionMode === 'category') {
      sourceData = therapeuticData;
    } else if (dimensionMode === 'route') {
      sourceData = routeData;
    } else if (dimensionMode === 'distribution') {
      sourceData = distributionData;
    } else if (dimensionMode === 'drugType') {
      sourceData = drugTypeData;
    } else if (dimensionMode === 'mechanism') {
      sourceData = mechanismData;
    } else if (dimensionMode === 'smallMolecule') {
      sourceData = smallMoleculeData;
    } else if (dimensionMode === 'prescriptionStatus') {
      sourceData = prescriptionStatusData;
    } else if (dimensionMode === 'patentStatus') {
      sourceData = patentStatusData;
    }
    
    // Get the appropriate color scheme based on therapeutic area and dimension
    const colors = getColorScheme(filters.therapeuticArea || 'All', dimensionMode);
    
    if (!sourceData || sourceData.length === 0) return [];
    
    // Always use the end year of the current Year Range
    const endYear = Array.isArray(filters.yearRange) ? filters.yearRange[1] : 2025;
      
    const yearData = sourceData.find(data => data.year === endYear);
    if (!yearData) return [];
    
    const dimensionHierarchy = (dimensionMode === 'class' ? hierarchies['drug_classes'] :
                               dimensionMode === 'category' ? hierarchies['therapeutic_categories'] :
                               dimensionMode === 'route' ? hierarchies['routes_of_administration'] : {}) || {};
    
    // Calculate total without double-counting (exclude children whose parent is also present)
    const total = Object.keys(yearData)
      .filter(key => key !== 'year' && !dimensionHierarchy[key])
      .reduce((sum, key) => sum + (yearData[key] || 0), 0);
    
    if (total === 0) return [];
    
    const result: Array<{ name: string; value: number; share: number; color: string; fullName: string }> = [];

    // Helper to add a pie slice entry
    const addSlice = (itemName: string) => {
      const value = yearData[itemName] || 0;
      if (value > 0) {
        result.push({
          name: createMultiLineLabel(itemName),
          value,
          share: (value / total) * 100,
          color: colors[itemName] || '#8884d8',
          fullName: itemName
        });
      }
    };

    if (dimensionMode === 'class') {
      const reverseH = buildReverseHierarchy('drug_classes');
      if (filters.drugClasses.length > 0) {
        // Filter mode: expand parents to children for drill-down
        filters.drugClasses.forEach((drugClass: string) => {
          const children = reverseH[drugClass];
          if (children && children.length > 0) {
            children.forEach(child => addSlice(child));
          } else {
            addSlice(drugClass);
          }
        });
      } else {
        // Default: show parent-level items only (pie chart stays clean)
        Object.keys(yearData).forEach(key => {
          if (key !== 'year' && !dimensionHierarchy[key]) {
            addSlice(key);
          }
        });
      }
    } else if (dimensionMode === 'category') {
      const reverseH = buildReverseHierarchy('therapeutic_categories');
      if (filters.therapeuticCategories.length > 0) {
        // Filter mode: expand parents to children for drill-down
        filters.therapeuticCategories.forEach((category: string) => {
          const children = reverseH[category];
          if (children && children.length > 0) {
            children.forEach(child => addSlice(child));
          } else {
            addSlice(category);
          }
        });
      } else {
        // Default: show parent-level items only
        Object.keys(yearData).forEach(key => {
          if (key !== 'year' && !dimensionHierarchy[key]) {
            addSlice(key);
          }
        });
      }
    } else if (dimensionMode === 'route') {
      const reverseH = buildReverseHierarchy('routes_of_administration');
      if (filters.routesOfAdministration.length > 0) {
        // Filter mode: expand parents to children for drill-down
        filters.routesOfAdministration.forEach((route: string) => {
          const children = reverseH[route];
          if (children && children.length > 0) {
            children.forEach(child => addSlice(child));
          } else {
            addSlice(route);
          }
        });
      } else {
        // Default: show parent-level items only
        Object.keys(yearData).forEach(key => {
          if (key !== 'year' && !dimensionHierarchy[key]) {
            addSlice(key);
          }
        });
      }
    } else if (dimensionMode === 'distribution') {
      if (filters.distributionChannels.length > 0) {
        // Show only selected distribution channels
        filters.distributionChannels.forEach((channel: string) => {
          const value = yearData[channel] || 0;
          if (value > 0) {
            result.push({
              name: createMultiLineLabel(channel),
              value,
              share: (value / total) * 100,
              color: colors[channel] || '#8884d8',
              fullName: channel
            });
          }
        });
      } else {
        // Show all distribution channels
        Object.keys(yearData).forEach(key => {
          if (key !== 'year') {
            const value = yearData[key] || 0;
            if (value > 0) {
              result.push({
                name: createMultiLineLabel(key),
                value,
                share: (value / total) * 100,
                color: colors[key] || '#8884d8',
                fullName: key
              });
            }
          }
        });
      }
    } else if (dimensionMode === 'drugType') {
      if (filters.drugTypes.length > 0) {
        // Show only selected drug types
        filters.drugTypes.forEach((drugType: string) => {
          const value = yearData[drugType] || 0;
          if (value > 0) {
            result.push({
              name: createMultiLineLabel(drugType),
              value,
              share: (value / total) * 100,
              color: colors[drugType] || '#8884d8',
              fullName: drugType
            });
          }
        });
      } else {
        // Show all drug types
        Object.keys(yearData).forEach(key => {
          if (key !== 'year') {
            const value = yearData[key] || 0;
            if (value > 0) {
              result.push({
                name: createMultiLineLabel(key),
                value,
                share: (value / total) * 100,
                color: colors[key] || '#8884d8',
                fullName: key
              });
            }
          }
        });
      }
    } else if (dimensionMode === 'mechanism') {
      if (filters.mechanismsOfAction.length > 0) {
        // Show only selected mechanisms of action
        filters.mechanismsOfAction.forEach((mechanism: string) => {
          const value = yearData[mechanism] || 0;
          if (value > 0) {
            result.push({
              name: createMultiLineLabel(mechanism),
              value,
              share: (value / total) * 100,
              color: colors[mechanism] || '#8884d8',
              fullName: mechanism
            });
          }
        });
      } else {
        // Show all mechanisms of action
        Object.keys(yearData).forEach(key => {
          if (key !== 'year') {
            const value = yearData[key] || 0;
            if (value > 0) {
              result.push({
                name: createMultiLineLabel(key),
                value,
                share: (value / total) * 100,
                color: colors[key] || '#8884d8',
                fullName: key
              });
            }
          }
        });
      }
    } else if (dimensionMode === 'smallMolecule') {
      if (filters.smallMoleculeDrugClasses.length > 0) {
        // Show only selected small molecule drug classes
        filters.smallMoleculeDrugClasses.forEach((smallMoleculeClass: string) => {
          const value = yearData[smallMoleculeClass] || 0;
          if (value > 0) {
            result.push({
              name: createMultiLineLabel(smallMoleculeClass),
              value,
              share: (value / total) * 100,
              color: colors[smallMoleculeClass] || '#8884d8',
              fullName: smallMoleculeClass
            });
          }
        });
      } else {
        // Show all small molecule drug classes
        Object.keys(yearData).forEach(key => {
          if (key !== 'year') {
            const value = yearData[key] || 0;
            if (value > 0) {
              result.push({
                name: createMultiLineLabel(key),
                value,
                share: (value / total) * 100,
                color: colors[key] || '#8884d8',
                fullName: key
              });
            }
          }
        });
      }
    } else if (dimensionMode === 'prescriptionStatus') {
      if (filters.prescriptionStatuses.length > 0) {
        // Show only selected prescription statuses
        filters.prescriptionStatuses.forEach((status: string) => {
          const value = yearData[status] || 0;
          if (value > 0) {
            result.push({
              name: createMultiLineLabel(status),
              value,
              share: (value / total) * 100,
              color: colors[status] || '#8884d8',
              fullName: status
            });
          }
        });
      } else {
        // Show all prescription statuses
        Object.keys(yearData).forEach(key => {
          if (key !== 'year') {
            const value = yearData[key] || 0;
            if (value > 0) {
              result.push({
                name: createMultiLineLabel(key),
                value,
                share: (value / total) * 100,
                color: colors[key] || '#8884d8',
                fullName: key
              });
            }
          }
        });
      }
    } else if (dimensionMode === 'patentStatus') {
      if (filters.patentStatuses.length > 0) {
        // Show only selected patent statuses
        filters.patentStatuses.forEach((status: string) => {
          const value = yearData[status] || 0;
          if (value > 0) {
            result.push({
              name: createMultiLineLabel(status),
              value,
              share: (value / total) * 100,
              color: colors[status] || '#8884d8',
              fullName: status
            });
          }
        });
      } else {
        // Show all patent statuses
        Object.keys(yearData).forEach(key => {
          if (key !== 'year') {
            const value = yearData[key] || 0;
            if (value > 0) {
              result.push({
                name: createMultiLineLabel(key),
                value,
                share: (value / total) * 100,
                color: colors[key] || '#8884d8',
                fullName: key
              });
            }
          }
        });
      }
    }
    
    return result.sort((a, b) => b.value - a.value);
  };

  // Get aggregated data using centralized utilities
  const totalMarketData = getTotalsForMode(filters, drugClassData, therapeuticData, routeData, distributionData, drugTypeData, mechanismData, smallMoleculeData, prescriptionStatusData, patentStatusData);
  const growthData = getYoYFromTotals(totalMarketData);
  const safeYearRange = Array.isArray(filters.yearRange) ? filters.yearRange : [2019, 2025];
  const cagrValue = getCagrFromTotals(totalMarketData, safeYearRange[0], safeYearRange[1]);
  const [showCAGR, setShowCAGR] = useState(false);

  // Get data for bottom cards
  const dimensionTrendData = getDimensionTrend(filters, dimensionMode);
  const dimensionEndYearCompositionData = getDimensionEndYearComposition(filters, dimensionMode);

  // Pre-compute vertical offsets so neighboring pie labels never overlap while
  // keeping every label inside the chart's vertical bounds. Splits each side
  // (left/right) into upper and lower halves and pushes labels outward from the
  // middle so the available vertical space is used symmetrically rather than
  // letting later labels fall off the bottom of the container.
  const labelDyMap = useMemo(() => {
    const result = new Map<string, number>();
    const data = dimensionEndYearCompositionData;
    if (!data || data.length === 0) return result;

    const total = data.reduce((sum: number, d: any) => sum + (d.value || 0), 0);
    if (total <= 0) return result;

    const RADIAN = Math.PI / 180;
    const refRadius = 100;
    const minSpacing = data.length > 6 ? 24 : 22;

    let cumulative = 0;
    const items = data.map((d: any, idx: number) => {
      const sliceAngle = ((d.value || 0) / total) * 360;
      const midAngle = cumulative + sliceAngle / 2;
      cumulative += sliceAngle;
      const x = Math.cos(-midAngle * RADIAN) * refRadius;
      const y = Math.sin(-midAngle * RADIAN) * refRadius;
      return { name: d.name, idx, x, y, onRight: x >= 0, dy: 0 };
    });

    [true, false].forEach(side => {
      const sideItems = items.filter(it => it.onRight === side);
      const upper = sideItems.filter(it => it.y < 0).sort((a, b) => b.y - a.y);
      const lower = sideItems.filter(it => it.y >= 0).sort((a, b) => a.y - b.y);

      for (let i = 1; i < upper.length; i++) {
        const prev = upper[i - 1];
        const curr = upper[i];
        const maxY = prev.y + prev.dy - minSpacing;
        if (curr.y + curr.dy > maxY) {
          curr.dy = maxY - curr.y;
        }
      }

      for (let i = 1; i < lower.length; i++) {
        const prev = lower[i - 1];
        const curr = lower[i];
        const minY = prev.y + prev.dy + minSpacing;
        if (curr.y + curr.dy < minY) {
          curr.dy = minY - curr.y;
        }
      }
    });

    items.forEach(it => result.set(it.name, it.dy));
    return result;
  }, [dimensionEndYearCompositionData]);
 
  const dimensionOptions = (
    filters.therapeuticArea === 'Psoriasis' ? [
      { label: 'Drug Class', value: 'class' },
      { label: 'Route of Administration', value: 'route' },
      { label: 'Distribution Channel', value: 'distribution' }
    ] : filters.therapeuticArea === 'Dermatology' ? [
      { label: 'Indication/Therapeutic Class', value: 'category' },
      { label: 'Route of Administration', value: 'route' },
      { label: 'Drug Type', value: 'drugType' },
      { label: 'Distribution Channel', value: 'distribution' },
      { label: 'Drug Class', value: 'class' },
      { label: 'Mechanism of Action (Biologics)', value: 'mechanism' },
      { label: 'Drug Class (Small Molecules)', value: 'smallMolecule' }
    ] : (filters.therapeuticArea === 'Retinal Disorders' || filters.therapeuticArea === 'Retinal - Focus') ? [
      { label: 'Indication', value: 'category' },
      { label: 'Drug Class', value: 'class' },
      { label: 'Route of Administration', value: 'route' },
      { label: 'Distribution Channel', value: 'distribution' }
    ] : (filters.therapeuticArea === 'Ophthalmology' || filters.therapeuticArea === 'Ophthalmology - In Sight') ? [
      { label: 'Indication', value: 'category' },
      { label: 'Route of Administration', value: 'route' },
      { label: 'Distribution Channel', value: 'distribution' },
      { label: 'Drug Type', value: 'drugType' },
      { label: 'Prescription Status', value: 'prescriptionStatus' },
      { label: 'Patent Status', value: 'patentStatus' }
    ] : (filters.therapeuticArea === 'Glaucoma' || filters.therapeuticArea === 'Glaucoma - Focus') ? [
      { label: 'Drug Class', value: 'class' },
      { label: 'Indication', value: 'category' },
      { label: 'Distribution Channel', value: 'distribution' },
      { label: 'Drug Type', value: 'drugType' }
    ] : (filters.therapeuticArea === 'Cell, Gene & RNA Therapies' || filters.therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight') ? [
      // CGT/RNA In Sight v1: two primary cuts of the same total market
      { label: 'Submarket', value: 'class' },
      { label: 'Therapeutic Area', value: 'category' }
    ] : [
      { label: 'Drug Class', value: 'class' },
      { label: 'Therapeutic Category', value: 'category' }
    ]
  );
  const selectedDimensionLabel = dimensionOptions.find(
    option => option.value === dimensionMode
  )?.label || 'Category';
  const trendTitle = `Trend by ${selectedDimensionLabel}`;
  const compositionTitle = `Composition by ${selectedDimensionLabel}`;
  const fastestGrowingLabel = `Fastest Growing ${selectedDimensionLabel}`;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading market data...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      {activeTab === 'market-overview' && (
        <Row gutter={[16, 16]}>
          {/* Market Size */}
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {isUnfilteredMode ? 'Total Market Size' : 
                   isClassMode ? `Market Size - ${filters.drugClasses.join(', ')}` :
                   isCategoryMode ? `Market Size - ${filters.therapeuticCategories.join(', ')}` :
                   isRouteMode ? `Market Size - ${filters.routesOfAdministration.join(', ')}` :
                   isDistributionMode ? `Market Size - ${filters.distributionChannels.join(', ')}` :
                   isDrugTypeMode ? `Market Size - ${filters.drugTypes.join(', ')}` :
                   isMechanismMode ? `Market Size - ${filters.mechanismsOfAction.join(', ')}` :
                   isSmallMoleculeMode ? `Market Size - ${filters.smallMoleculeDrugClasses.join(', ')}` :
                   isPrescriptionStatusMode ? `Market Size - ${filters.prescriptionStatuses.join(', ')}` :
                   isPatentStatusMode ? `Market Size - ${filters.patentStatuses.join(', ')}` :
                   'Market Size'}
                </Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {isUnfilteredMode ? 'Total market revenue over time' : 
                   isClassMode ? 'Filtered by drug class' : 
                   isCategoryMode ? 'Filtered by indication' :
                   isRouteMode ? 'Filtered by route of administration' :
                   isDistributionMode ? 'Filtered by distribution channel' :
                   isDrugTypeMode ? 'Filtered by drug type' :
                   isMechanismMode ? 'Filtered by mechanism of action' :
                   isSmallMoleculeMode ? 'Filtered by small molecule drug class' :
                   isPrescriptionStatusMode ? 'Filtered by prescription status' :
                   isPatentStatusMode ? 'Filtered by patent status' :
                   'Filtered view'}
                </Text>
              </div>
            }
            loading={chartLoading}
          >
            <FullscreenChart
              chartId="market-size"
              title="Market Size"
              isFullscreen={fullscreenChart === 'market-size'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'market-size' ? null : 'market-size')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={totalMarketData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                    <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div style={{
                              backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
                              border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
                              borderRadius: '8px',
                              padding: '12px 16px',
                              boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              fontSize: '14px',
                              fontWeight: 500
                            }}>
                              <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>Year: {label}</div>
                              {payload.map((entry: any, index: number) => (
                                <div key={index} style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '16px', fontWeight: 600 }}>
                                  {entry.name}: ${Number(entry.value).toFixed(1)}M
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="totalRevenue" fill="#1890ff" name="Total Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={totalMarketData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                  <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                  <Tooltip 
                    formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                    labelFormatter={(label) => `Year: ${label}`}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div style={{
                            backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
                            border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
                            borderRadius: '8px',
                            padding: '12px 16px',
                            boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            fontSize: '14px',
                            fontWeight: 500
                          }}>
                            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>Year: {label}</div>
                            {payload.map((entry: any, index: number) => (
                              <div key={index} style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '16px', fontWeight: 600 }}>
                                {entry.name}: ${Number(entry.value).toFixed(1)}M
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="totalRevenue" fill="#1890ff" name="Total Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </FullscreenChart>
          </Card>
        </Col>

            {/* Total Market Growth */}
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Total Market Growth</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Year-over-year growth trends
                </Text>
              </div>
            }
            loading={chartLoading}
            extra={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '40px' }}>
                <Text style={{ fontSize: '12px', color: isDark ? 'rgba(255, 255, 255, 0.65)' : undefined }}>View:</Text>
                <div style={{ display: 'flex', border: `1px solid ${isDark ? '#434343' : '#d9d9d9'}`, borderRadius: '6px', overflow: 'hidden' }}>
                  <button
                    onClick={() => setShowCAGR(false)}
                    style={{
                      padding: '4px 8px',
                      border: 'none',
                      background: !showCAGR ? '#1890ff' : (isDark ? '#1f1f1f' : '#f5f5f5'),
                      color: !showCAGR ? 'white' : (isDark ? 'rgba(255, 255, 255, 0.85)' : '#666'),
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: !showCAGR ? '500' : '400'
                    }}
                  >
                    YoY %
                  </button>
                  <button
                    onClick={() => setShowCAGR(true)}
                    style={{
                      padding: '4px 8px',
                      border: 'none',
                      background: showCAGR ? '#1890ff' : (isDark ? '#1f1f1f' : '#f5f5f5'),
                      color: showCAGR ? 'white' : (isDark ? 'rgba(255, 255, 255, 0.85)' : '#666'),
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: showCAGR ? '500' : '400'
                    }}
                  >
                    CAGR
                  </button>
                </div>
              </div>
            }
          >
            <FullscreenChart
              chartId="market-growth"
              title="Total Market Growth"
              isFullscreen={fullscreenChart === 'market-growth'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'market-growth' ? null : 'market-growth')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  {showCAGR ? (
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'flex-start', 
                      height: 'calc(100% - 80px)',
                      padding: '20px',
                      paddingTop: '40px'
                    }}>
                      {cagrValue !== null ? (
                        <>
                          <div style={{
                            background: '#f0f8ff',
                            border: '1px solid #91d5ff',
                            borderRadius: '12px',
                            padding: '40px 30px',
                            textAlign: 'center',
                            minWidth: '280px',
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.1)',
                            marginTop: 'auto',
                            marginBottom: 'auto'
                          }}>
                            <Title level={1} style={{ margin: 0, color: '#1890ff', fontSize: '48px', lineHeight: '1.2' }}>
                              {cagrValue.toFixed(1)}%
                            </Title>
                            <Text style={{ fontSize: '18px', color: '#666', marginTop: '16px', display: 'block' }}>
                              CAGR {totalMarketData[0]?.year}–{totalMarketData[totalMarketData.length - 1]?.year}
                            </Text>
                          </div>
                          <Text type="secondary" style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px' }}>
                            Compound Annual Growth Rate
                          </Text>
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', color: '#999' }}>
                          <Title level={4} style={{ margin: 0 }}>Not enough data</Title>
                          <Text>Need at least 2 years to compute CAGR</Text>
                        </div>
                      )}
                    </div>
                  ) : (
                    growthData.length < 2 ? (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        padding: '20px'
                      }}>
                        <div style={{ textAlign: 'center', color: '#999' }}>
                          <Title level={4} style={{ margin: 0 }}>Not enough data</Title>
                          <Text>Need at least 2 years to compute growth rates</Text>
                        </div>
                      </div>
                    ) : (
                      <LineChart data={growthData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="year" 
                          ticks={(() => {
                            if (growthData.length === 0) return [];
                            const years = growthData.map(d => d.year).sort((a, b) => a - b);
                            // Show odd years (2021, 2023, 2025...) like before
                            const ticks = years.filter((year) => year % 2 === 1);
                            const lastYear = years[years.length - 1];
                            // If last year is even and not included, add it
                            if (!ticks.includes(lastYear)) {
                              ticks.push(lastYear);
                            }
                            return ticks.sort((a, b) => a - b);
                          })()}
                          tick={{ style: getChartFontStyle(isDark) }}
                        />
                        <YAxis 
                          tickFormatter={(value) => `${value.toFixed(1)}%`}
                          domain={['dataMin - 1', 'dataMax + 1']}
                          label={{ 
                            value: 'YoY Growth (%)', 
                            angle: -90, 
                            position: 'insideLeft',
                            style: getChartFontStyle(isDark)
                          }}
                          tick={{ style: getChartFontStyle(isDark) }}
                        />
                        <Tooltip 
                          formatter={(value: any, name: string) => {
                            if (name === 'YoY Growth') {
                              return value !== null ? [`${value.toFixed(1)}%`, 'YoY Growth'] : ['—', 'YoY Growth'];
                            }
                            if (name === 'absoluteDelta') {
                              return value !== null ? [`$${Number(value).toFixed(1)}M`, 'Δ Revenue'] : ['—', 'Δ Revenue'];
                            }
                            return [`$${Number(value).toFixed(1)}M`, 'Revenue'];
                          }}
                          labelFormatter={(label) => `Year: ${label}`}
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div style={{
                                  backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
                                  border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
                                  borderRadius: '8px',
                                  padding: '12px 16px',
                                  boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                  fontSize: '14px',
                                  fontWeight: 500
                                }}>
                                  <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>Year: {label}</div>
                                  {payload.map((entry: any, index: number) => (
                                    <div key={index} style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '14px', marginTop: '4px' }}>
                                      {entry.name === 'yoyGrowth' ? 'YoY Growth' : entry.name}: {entry.value !== null ? (entry.name === 'yoyGrowth' ? `${Number(entry.value).toFixed(1)}%` : entry.name === 'absoluteDelta' ? `$${Number(entry.value).toFixed(1)}M` : `$${Number(entry.value).toFixed(1)}M`) : '—'}
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="yoyGrowth" 
                          stroke="#1890ff" 
                          strokeWidth={3}
                          dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                          connectNulls={false}
                          name="YoY Growth"
                        />
                      </LineChart>
                    )
                  )}
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={300}>
                {showCAGR ? (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'flex-start', 
                    height: 'calc(100% - 80px)',
                    padding: '20px',
                    paddingTop: '40px'
                  }}>
                    {cagrValue !== null ? (
                      <>
                        <div style={{
                          background: isDark ? '#141414' : '#f0f8ff',
                          border: `1px solid ${isDark ? '#1890ff' : '#91d5ff'}`,
                          borderRadius: '12px',
                          padding: '40px 30px',
                          textAlign: 'center',
                          minWidth: '280px',
                          boxShadow: isDark ? '0 2px 12px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(24, 144, 255, 0.1)',
                          marginTop: 'auto',
                          marginBottom: 'auto'
                        }}>
                          <Title level={1} style={{ margin: 0, color: '#1890ff', fontSize: '48px', lineHeight: '1.2' }}>
                            {cagrValue.toFixed(1)}%
                          </Title>
                          <Text style={{ fontSize: '18px', color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666', marginTop: '16px', display: 'block' }}>
                            CAGR {totalMarketData[0]?.year}–{totalMarketData[totalMarketData.length - 1]?.year}
                          </Text>
                        </div>
                        <Text type="secondary" style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px' }}>
                          Compound Annual Growth Rate
                        </Text>
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        <Title level={4} style={{ margin: 0 }}>Not enough data</Title>
                        <Text>Need at least 2 years to compute CAGR</Text>
                      </div>
                    )}
                  </div>
                ) : (
                  growthData.length < 2 ? (
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      padding: '20px'
                    }}>
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        <Title level={4} style={{ margin: 0 }}>Not enough data</Title>
                        <Text>Need at least 2 years to compute growth rates</Text>
                      </div>
                    </div>
                  ) : (
                    <LineChart data={growthData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="year" 
                        ticks={(() => {
                          if (growthData.length === 0) return [];
                          const years = growthData.map(d => d.year).sort((a, b) => a - b);
                          // Show odd years (2021, 2023, 2025...) like before
                          const ticks = years.filter((year) => year % 2 === 1);
                          const lastYear = years[years.length - 1];
                          // If last year is even and not included, add it
                          if (!ticks.includes(lastYear)) {
                            ticks.push(lastYear);
                          }
                          return ticks.sort((a, b) => a - b);
                        })()}
                        tick={{ style: getChartFontStyle(isDark) }}
                      />
                      <YAxis 
                        tickFormatter={(value) => `${value.toFixed(1)}%`}
                        domain={['dataMin - 1', 'dataMax + 1']}
                        label={{ 
                          value: 'YoY Growth (%)', 
                          angle: -90, 
                          position: 'insideLeft',
                          style: getChartFontStyle(isDark)
                        }}
                        tick={{ style: getChartFontStyle(isDark) }}
                      />
                      <Tooltip 
                        formatter={(value: any, name: string) => {
                          if (name === 'YoY Growth') {
                            return value !== null ? [`${value.toFixed(1)}%`, 'YoY Growth'] : ['—', 'YoY Growth'];
                          }
                          if (name === 'absoluteDelta') {
                            return value !== null ? [`$${Number(value).toFixed(1)}M`, 'Δ Revenue'] : ['—', 'Δ Revenue'];
                          }
                          return [`$${Number(value).toFixed(1)}M`, 'Revenue'];
                        }}
                        labelFormatter={(label) => `Year: ${label}`}
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div style={{
                                backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
                                border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
                                borderRadius: '8px',
                                padding: '12px 16px',
                                boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                fontSize: '14px',
                                fontWeight: 500
                              }}>
                                <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>Year: {label}</div>
                                {payload.map((entry: any, index: number) => (
                                  <div key={index} style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '14px', marginTop: '4px' }}>
                                    {entry.name === 'yoyGrowth' ? 'YoY Growth' : entry.name}: {entry.value !== null ? (entry.name === 'yoyGrowth' ? `${Number(entry.value).toFixed(1)}%` : entry.name === 'absoluteDelta' ? `$${Number(entry.value).toFixed(1)}M` : `$${Number(entry.value).toFixed(1)}M`) : '—'}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="yoyGrowth" 
                        stroke="#1890ff" 
                        strokeWidth={3}
                        dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                        connectNulls={false}
                        name="YoY Growth"
                      />
                    </LineChart>
                  )
                )}
              </ResponsiveContainer>
            </FullscreenChart>
          </Card>
            </Col>
          </Row>
      )}

      {activeTab === 'market-breakdowns' && (
        <Row gutter={[16, 16]}>
          {/* Breakdown Trend */}
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <Card 
            className="market-overview-card"
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {trendTitle}
                </Title>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '12px', 
                  marginTop: '12px',
                  width: '100%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Dimension:</Text>
                    {filters.therapeuticArea === 'Dermatology' ? (
                      <Select
                        size="small"
                        value={dimensionMode}
                        onChange={(value) => setDimensionMode(value as any)}
                        disabled={locked}
                        style={{ width: 240 }}
                        options={dimensionOptions}
                      />
                    ) : (
                      <Segmented
                        size="small"
                        className="fit-content-segmented"
                        options={dimensionOptions}
                        value={dimensionMode}
                        onChange={(value) => setDimensionMode(value as any)}
                        disabled={locked}
                      />
                    )}
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'flex-start',
                    width: '100%',
                    minHeight: '36px',
                    paddingTop: '4px'
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>View:</Text>
                    <Segmented
                      size="small"
                      className="fit-content-segmented"
                      options={[
                        { label: 'Bars', value: 'bars' },
                        { label: 'Area', value: 'area' }
                      ]}
                      value={chartView}
                      onChange={(value) => setChartView(value as 'bars' | 'area')}
                    />
                  </div>
                </div>
              </div>
            }
            loading={chartLoading}
          >
            <FullscreenChart
              chartId="breakdown-trend"
              title="Breakdown Trend"
              isFullscreen={fullscreenChart === 'breakdown-trend'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'breakdown-trend' ? null : 'breakdown-trend')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  {chartView === 'bars' ? (
                    <BarChart data={dimensionTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                      <YAxis tickFormatter={(value) => `$${value}M`} tick={{ style: getChartFontStyle(isDark) }} />
                       <Tooltip
                         formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                         labelFormatter={(label) => `Year: ${label}`}
                         content={renderBreakdownTrendTooltip}
                       />
                      <Legend wrapperStyle={legendStyle} />
                      {dimensionTrendData.length > 0 && (() => {
                        const colors = getColorScheme(filters.therapeuticArea || 'All', dimensionMode);
                        const stackGrps = getStackGroups(dimensionMode);
                        return Object.keys(dimensionTrendData[0])
                          .filter(key => key !== 'year')
                          .map((key) => (
                            <Bar 
                              key={key}
                              dataKey={key} 
                              fill={colors[key] || '#8884d8'}
                              stackId={stackGrps[key] || undefined}
                            />
                          ));
                      })()}
                    </BarChart>
                  ) : (
                    <AreaChart data={dimensionTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                      <YAxis tickFormatter={(value) => `$${value}M`} tick={{ style: getChartFontStyle(isDark) }} />
                       <Tooltip
                         formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                         labelFormatter={(label) => `Year: ${label}`}
                         content={renderBreakdownTrendTooltip}
                       />
                      <Legend wrapperStyle={legendStyle} />
                      {dimensionTrendData.length > 0 && (() => {
                        const dataKeys = Object.keys(dimensionTrendData[0]).filter(key => key !== 'year');
                        const colors = getColorScheme(filters.therapeuticArea || 'All', dimensionMode);
                        
                        // Sort keys by their average value across all years in descending order
                        const sortedKeys = dataKeys.sort((a, b) => {
                          const avgA = dimensionTrendData.reduce((sum, yearData) => sum + (yearData[a] || 0), 0) / dimensionTrendData.length;
                          const avgB = dimensionTrendData.reduce((sum, yearData) => sum + (yearData[b] || 0), 0) / dimensionTrendData.length;
                          return avgB - avgA;
                        });
                        
                        return sortedKeys.map((key) => {
                          const color = colors[key] || '#8884d8';
                          return (
                            <Area 
                              key={key}
                              type="monotone"
                              dataKey={key} 
                              stackId="a"
                              stroke={color}
                              fill={color}
                              fillOpacity={0.8}
                            />
                          );
                        });
                      })()}
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={300}>
                {chartView === 'bars' ? (
                  <BarChart data={dimensionTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                      <YAxis tickFormatter={(value) => `$${value}M`} tick={{ style: getChartFontStyle(isDark) }} />
                       <Tooltip
                         formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                         labelFormatter={(label) => `Year: ${label}`}
                         content={renderBreakdownTrendTooltip}
                       />
                    <Legend wrapperStyle={legendStyle} />
                    {dimensionTrendData.length > 0 && (() => {
                      const colors = getColorScheme(filters.therapeuticArea || 'All', dimensionMode);
                      const stackGrps = getStackGroups(dimensionMode);
                      return Object.keys(dimensionTrendData[0])
                        .filter(key => key !== 'year')
                        .map((key) => (
                          <Bar 
                            key={key}
                            dataKey={key} 
                            fill={colors[key] || '#8884d8'}
                            stackId={stackGrps[key] || undefined}
                          />
                        ));
                    })()}
                  </BarChart>
                ) : (
                  <AreaChart data={dimensionTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                      <YAxis tickFormatter={(value) => `$${value}M`} tick={{ style: getChartFontStyle(isDark) }} />
                       <Tooltip
                         formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                         labelFormatter={(label) => `Year: ${label}`}
                         content={renderBreakdownTrendTooltip}
                       />
                    <Legend wrapperStyle={legendStyle} />
                    {dimensionTrendData.length > 0 && (() => {
                      const dataKeys = Object.keys(dimensionTrendData[0]).filter(key => key !== 'year');
                      const colors = getColorScheme(filters.therapeuticArea || 'All', dimensionMode);
                      
                      // Sort keys by their average value across all years in descending order
                      const sortedKeys = dataKeys.sort((a, b) => {
                        const avgA = dimensionTrendData.reduce((sum, yearData) => sum + (yearData[a] || 0), 0) / dimensionTrendData.length;
                        const avgB = dimensionTrendData.reduce((sum, yearData) => sum + (yearData[b] || 0), 0) / dimensionTrendData.length;
                        return avgB - avgA;
                      });
                      
                      return sortedKeys.map((key) => {
                        const color = colors[key] || '#8884d8';
                        return (
                          <Area 
                            key={key}
                            type="monotone"
                            dataKey={key} 
                            stackId="a"
                            stroke={color}
                            fill={color}
                            fillOpacity={0.8}
                          />
                        );
                      });
                    })()}
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </FullscreenChart>
          </Card>
            </Col>

            {/* Composition (End-Year) */}
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <Card 
            className="market-overview-card"
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {compositionTitle}
                </Title>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '12px', 
                  marginTop: '12px',
                  width: '100%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Dimension:</Text>
                    {filters.therapeuticArea === 'Dermatology' ? (
                      <Select
                        size="small"
                        value={dimensionMode}
                        onChange={(value) => setDimensionMode(value as any)}
                        disabled={locked}
                        style={{ width: 240 }}
                        options={dimensionOptions}
                      />
                    ) : (
                      <Segmented
                        size="small"
                        className="fit-content-segmented"
                        options={dimensionOptions}
                        value={dimensionMode}
                        onChange={(value) => setDimensionMode(value as any)}
                        disabled={locked}
                      />
                    )}
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'flex-start',
                    width: '100%',
                    minHeight: '36px',
                    paddingTop: '5px'
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Year: {Array.isArray(filters.yearRange) ? filters.yearRange[1] : 2025}
                    </Text>
                  </div>
                </div>
              </div>
            }
            loading={chartLoading}
          >
            <FullscreenChart
              chartId="composition-end-year"
              title="Composition (End-Year)"
              isFullscreen={fullscreenChart === 'composition-end-year'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'composition-end-year' ? null : 'composition-end-year')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  {dimensionEndYearCompositionData.length > 0 ? (
                    <PieChart margin={
                      isSmallMoleculeView
                        ? { top: 40, right: 60, left: 60, bottom: 40 }
                        : { top: 30, right: 40, left: 40, bottom: 30 }
                    }>
                      <Pie
                        data={dimensionEndYearCompositionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={<CustomLabel />}
                        innerRadius={60}
                        outerRadius={isSmallMoleculeView ? 100 : 120}
                        paddingAngle={2}
                        dataKey="value"
                        animationBegin={0}
                        animationDuration={800}
                        animationEasing="ease-out"
                      >
                        {dimensionEndYearCompositionData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.color}
                            stroke="#fff"
                            strokeWidth={2}
                            style={{
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                              transition: 'opacity 0.3s'
                            }}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    </PieChart>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      padding: '20px'
                    }}>
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        <Title level={4} style={{ margin: 0 }}>No Data Available</Title>
                        <Text>No data available for the selected year and dimension.</Text>
                      </div>
                    </div>
                  )}
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={dimensionEndYearCompositionData.length > 6 ? 380 : 300}>
                  {dimensionEndYearCompositionData.length > 0 ? (
                    <PieChart margin={
                      isSmallMoleculeView
                        ? { top: 30, right: 50, left: 50, bottom: 30 }
                        : { top: 25, right: 35, left: 35, bottom: 25 }
                    }>
                      <Pie
                      data={dimensionEndYearCompositionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={<CustomLabel />}
                      innerRadius={30}
                        outerRadius={isSmallMoleculeView ? 70 : 80}
                      paddingAngle={2}
                      dataKey="value"
                      animationBegin={0}
                      animationDuration={800}
                      animationEasing="ease-out"
                    >
                      {dimensionEndYearCompositionData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          stroke="#fff"
                          strokeWidth={2}
                          style={{
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                            transition: 'opacity 0.3s'
                          }}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  </PieChart>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    padding: '20px'
                  }}>
                    <div style={{ textAlign: 'center', color: '#999' }}>
                      <Title level={4} style={{ margin: 0 }}>No Data Available</Title>
                      <Text>No data available for the selected year and dimension.</Text>
                    </div>
                  </div>
                )}
              </ResponsiveContainer>
            </FullscreenChart>
          </Card>
            </Col>
          </Row>
      )}

      {/* Key Insights - Only show if one of the relevant tabs is active */}
      {(activeTab === 'market-overview' || activeTab === 'market-breakdowns') && (
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card title="Key Market Insights">
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  {(() => {
                    // Calculate fastest growing category/class based on therapeutic area
                    if (totalMarketData.length < 2) {
                      return (
                        <>
                          <Title level={4} style={{ color: '#1890ff', margin: 0 }}>N/A</Title>
                          <Text type="secondary">
                            {fastestGrowingLabel}
                          </Text>
                        </>
                      );
                    }
                    
                    const firstYear = totalMarketData[0];
                    const lastYear = totalMarketData[totalMarketData.length - 1];
                    const years = lastYear.year - firstYear.year;
                    
                    if (years === 0) {
                      return (
                        <>
                          <Title level={4} style={{ color: '#1890ff', margin: 0 }}>N/A</Title>
                          <Text type="secondary">
                            {fastestGrowingLabel}
                          </Text>
                        </>
                      );
                    }
                    
                    let fastestCategory = '';
                    let fastestCAGR = -Infinity;
                    
                    // Get the appropriate data source based on dimension mode
                    const sourceData = dimensionMode === 'class' ? drugClassData : 
                                     dimensionMode === 'category' ? therapeuticData :
                                     dimensionMode === 'route' ? routeData : distributionData;
                    
                    if (sourceData.length > 0) {
                      const categories = Object.keys(sourceData[0]).filter(key => key !== 'year');
                      
                      categories.forEach(category => {
                        const startValue = sourceData[0][category] || 0;
                        const endValue = sourceData[sourceData.length - 1][category] || 0;
                        if (startValue > 0) {
                          const cagr = (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
                          if (cagr > fastestCAGR) {
                            fastestCAGR = cagr;
                            fastestCategory = category;
                          }
                        }
                      });
                    }
                    
                    if (fastestCAGR <= -Infinity) {
                      return (
                        <>
                          <Title level={4} style={{ color: '#1890ff', margin: 0 }}>N/A</Title>
                          <Text type="secondary">
                            {fastestGrowingLabel}
                          </Text>
                        </>
                      );
                    }
                    
                    return (
                      <>
                        <Title level={4} style={{ color: '#1890ff', margin: 0 }}>
                          {fastestCAGR.toFixed(1)}%
                        </Title>
                        <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
                          {fastestCategory}
                        </Text>
                        <Text type="secondary" style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}>
                          {fastestGrowingLabel}
                        </Text>
                      </>
                    );
                  })()}
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#52c41a', margin: 0 }}>
                    {(() => {
                      // Calculate end-year market projection from actual data
                      if (totalMarketData.length === 0) return 'N/A';
                      
                      const endYearData = totalMarketData[totalMarketData.length - 1];
                      const endYearValue = endYearData.totalRevenue;
                      
                      if (endYearValue >= 1000) {
                        return `$${(endYearValue / 1000).toFixed(1)}B`;
                      } else {
                        return `$${endYearValue.toFixed(0)}M`;
                      }
                    })()}
                  </Title>
                  <Text type="secondary">
                    {totalMarketData.length > 0 ? 
                      `${totalMarketData[totalMarketData.length - 1].year} Market Size` : 
                      'End-Year Projection'
                    }
                  </Text>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#faad14', margin: 0 }}>
                    {(() => {
                      // Calculate overall CAGR from actual market data
                      if (cagrValue !== null) {
                        return `${cagrValue.toFixed(1)}%`;
                      }
                      return 'N/A';
                    })()}
                  </Title>
                  <Text type="secondary">
                    {totalMarketData.length > 0 ? 
                      `Overall CAGR ${totalMarketData[0].year}-${totalMarketData[totalMarketData.length - 1].year}` : 
                      'Overall CAGR'
                    }
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      )}
    </div>
  );
};

export default MarketOverview;